import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.function.name.js';
import 'core-js/modules/es.object.assign.js';
import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import React from 'react';
import { Switch as Switch$1 } from '@mui/base/Switch';
import classNames from 'classnames';
import Color from 'color';
import { getThemeClassNames } from '@cognite/cogs-utils';
import { Flex } from '../flex/flex.js';
import { Skeleton } from '../skeleton/skeleton.js';

/**
 * Switch
 * =============================================================================
 * Switches toggle the state of a single item on or off.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-switch--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Switch checked />
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param checked         - Check or uncheck the checkbox.
 * @param color           - The color of the checkbox. Dont over-use this!
 * @param defaultChecked  - If true, the checkbox is checked by default.
 * @param disabled        - Disable the checkbox.
 * @param name            - Native `name` HTML attribute.
 * @param onChange        - The function fired when the checkbox is changed.
 * @param readOnly        - If true, the user can't change the checkbox.
 * @param skeleton        - The skeleton version of the switch.
 * @param style           - Overwrite and extend the default styling with custom styles.
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-switch--base)
 */
var Switch = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var className = _a.className,
    color = _a.color,
    disabled = _a.disabled,
    inverted = _a.inverted,
    _onChange = _a.onChange,
    _a$size = _a.size,
    size = _a$size === undefined ? 'x-small' : _a$size,
    style = _a.style,
    checked = _a.checked,
    defaultChecked = _a.defaultChecked,
    readOnly = _a.readOnly,
    _a$skeleton = _a.skeleton,
    skeleton = _a$skeleton === undefined ? false : _a$skeleton,
    label = _a.label,
    name = _a.name,
    id = _a.id,
    themeProps = _a.themeProps,
    delegatedProps = __rest(_a, ["className", "color", "disabled", "inverted", "onChange", "size", "style", "checked", "defaultChecked", "readOnly", "skeleton", "label", "name", "id", "themeProps"]);
  return skeleton ? jsxs(Flex, {
    direction: "row",
    gap: 10,
    children: [jsx(Skeleton.Rectangle, {
      width: size === 'x-small' ? '36px' : '28px',
      height: size === 'x-small' ? '20px' : '16px',
      rounded: true
    }), label && jsx(Skeleton.Rectangle, {
      width: "100px",
      height: size === 'x-small' ? '20px' : '16px'
    })]
  }) : jsxs("label", {
    className: classNames.apply(undefined, ['cogs-v10', 'cogs-switch', "cogs-switch--size-" + size, disabled && "cogs-switch--disabled"].concat(getThemeClassNames(inverted, themeProps), [className])),
    htmlFor: name,
    ref: ref,
    style: color ? Object.assign({
      '--Switch-checked': Color(color).hex(),
      '--Switch-checked-hover': Color(color).darken(0.05).hex(),
      '--Switch-checked-pressed': Color(color).darken(0.1).hex(),
      '--Switch-unchecked-hover': Color(color).lighten(0.1).hex(),
      '--Switch-unchecked-pressed': Color(color).hex()
    }, style) : Object.assign({}, style),
    children: [jsx(Switch$1, {
      id: id,
      className: "cogs-v10 cogs-switch__root",
      disabled: disabled,
      onChange: function onChange(event) {
        return _onChange && _onChange(event, event.target.checked);
      },
      checked: checked,
      defaultChecked: defaultChecked,
      readOnly: readOnly,
      slot: "span",
      slotProps: {
        thumb: {
          className: 'cogs-switch__thumb'
        },
        track: {
          className: 'cogs-switch__track'
        },
        input: Object.assign({
          id: name,
          'aria-describedby': name,
          'aria-label': label,
          name: name,
          className: 'cogs-switch__input',
          role: 'switch'
        }, delegatedProps)
      }
    }), label && jsx("span", {
      className: "cogs-v10 cogs-switch__label",
      children: label
    })]
  });
});

export { Switch };
